import React, { useEffect, useState } from "react"
import { Input, Button, message, Alert } from "antd"
import { gen } from "./utils"
import { useHistory, useLocation } from "react-router-dom"
import PasswordResetEmail from "./PasswordResetEmail"

type PasswordResetProps = {
  inline?: boolean // when true, render only the form so it can be embedded
  initialEmail?: string
  onCancel?: () => void
  onSuccess?: () => void
  token?: string | null
}

type LocationState = {
  token?: string | null
}

const emailIsValid = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export default function PasswordReset({
  inline = false,
  initialEmail,
  onCancel,
  onSuccess,
  token: propToken,
}: PasswordResetProps): JSX.Element {
  const [sent, setSent] = useState(false)
  const history = useHistory()
  const location = useLocation<LocationState>()
  const token = propToken ?? location?.state?.token ?? undefined

  const [validEmail, setValidEmail] = useState<boolean>(true)
  const [invalidEmailMessage, setInvalidEmailMessage] = useState<string | null>(null)
  const [email, setEmail] = useState<string>(initialEmail ?? "")

  const [validPassword, setValidPassword] = useState<boolean>(true)
  const [invalidPasswordMessage, setInvalidPasswordMessage] = useState<string | null>(null)
  const [password, setPassword] = useState<string>("")
  const [password2, setPassword2] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail)
  }, [initialEmail])

  const onSubmit = async () => {
    let thereIsAProblem = false
    setValidEmail(true)
    setInvalidEmailMessage(null)
    setValidPassword(true)
    setInvalidPasswordMessage(null)

    if (!emailIsValid(email)) {
      setValidEmail(false)
      setInvalidEmailMessage("Something looks wrong with this email")
      thereIsAProblem = true
    }
    if (/\s/.test(email)) {
      setValidEmail(false)
      setInvalidEmailMessage("Uh oh, your email can't have a space in it.")
      thereIsAProblem = true
    }

    // Only validate passwords when token is present (i.e. user coming from reset link)
    if (token) {
      if (password.trim() !== password) {
        setValidPassword(false)
        setInvalidPasswordMessage("Password shouldn't start or end with a space")
        thereIsAProblem = true
      }
      if (password !== password2) {
        setValidPassword(false)
        setInvalidPasswordMessage("Passwords don't match")
        thereIsAProblem = true
      }
    }

    if (thereIsAProblem) return

    setLoading(true)

    const data: any = {
      email,
      token,
    }
    if (token) {
      data.password = password
      data.password_confirmation = password2
    }

    try {
      const endpoint = token ? `/api/passwordReset` : `/api/passwordResetEmail`
      const response = await fetch(gen(endpoint), {
        method: "post",
        body: JSON.stringify(data),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
      })

      const result = await response.json()
      if (result?.error) {
        message.error(result.message || "Failed to reset password")
        setLoading(false)
        return
      }

      setLoading(false)

      // handle response for both flows
      if (!token) {
        // send-email flow
        setSent(true)
        if (onSuccess) onSuccess()
        else
          message.success(
            result?.message ||
              "If that email address has a Swimmingly Clubhouse account, a password reset email has been sent. Please check your inbox & follow instructions."
          )
      } else {
        // token/password flow
        if (onSuccess) onSuccess()
        else {
          message.success(result?.message || "Password successfully changed")
          history.push("/")
        }
      }
    } catch (err) {
      console.error(err)
      setLoading(false)
      message.error("Server error")
    }
  }

  // Render only the form so this component can be embedded
  const formContent = (
    <div style={{ width: "100%" }}>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email Address"
        style={{ marginBottom: 8 }}
      />
      {!validEmail ? (
        <Alert
          title={invalidEmailMessage ?? "fix email"}
          type="error"
          closable
          style={{ marginTop: 8, marginBottom: 8 }}
        />
      ) : null}

      {token && (
        <>
          <Input
            value={password}
            style={{ marginTop: 8 }}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            placeholder="Password"
          />
          <Input
            value={password2}
            style={{ marginTop: 8 }}
            type="password"
            onChange={(e) => setPassword2(e.target.value)}
            placeholder="Password (repeat)"
          />
          {!validPassword ? (
            <Alert
              title={invalidPasswordMessage ?? "fix password"}
              type="error"
              closable
              style={{ marginTop: 8 }}
            />
          ) : null}
        </>
      )}

      <Button
        style={{ width: "100%", marginTop: 12 }}
        type="primary"
        onClick={onSubmit}
        loading={loading}
      >
        {token ? "Set New Password" : "Send Password Reset Email"}
      </Button>

      {inline && onCancel ? (
        <div style={{ marginTop: 10, textAlign: "center" }}>
          <Button type="link" onClick={onCancel}>
            Back to sign in
          </Button>
        </div>
      ) : null}
    </div>
  )

  if (inline && sent) {
    // render the confirmation UI inline (no route needed)
    return <PasswordResetEmail email={email} />
  }

  // original standalone layout (kept for backward compatibility)
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: "30px" }}>
      <section style={{ width: "350px" }}>
        <div>{formContent}</div>
      </section>
    </div>
  )
}